// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const ExecutionQueueView = ({ onNavigate = undefined }: { onNavigate?: any }) => {
  const [queue, setQueue] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState<any>(null);
  const [selected, setSelected] = useState(new Set());
  const [awaitingUploading, setAwaitingUploading] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const { selected: clientScopeSelected } = useClientScope();

  const loadQueue = async () => {
    setLoading(true);
    const data = await apiCall("/esign/execution-queue");
    if (data?.queue) setQueue(data.queue);
    setSelected(new Set());
    setLoading(false);
  };

  useEffect(() => { loadQueue(); }, [clientScopeSelected]);

  // Only 'completed' rows carry an executed document and can be bulk-imported;
  // 'awaiting' rows have no document yet (CAUSE 4).
  const completedRows = queue.filter(q => q.row_type !== 'awaiting');
  const toggleSelect = (id: string) => { setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }); };
  const toggleAll = () => { if (selected.size === completedRows.length) setSelected(new Set()); else setSelected(new Set(completedRows.map(q => q.id))); };

  // CAUSE 4: import a manually-supplied fully-executed copy for an awaiting
  // request. Routes through the standard AI-Import batch (which OCRs image-only
  // PDFs on its native→OCR fallback tier), tagged with source_request_id so the
  // Approve step advances the request to Fully Executed.
  const importExecuted = async (requestId: string, file: File) => {
    if (!file) return;
    setAwaitingUploading(requestId);
    try {
      const token = localStorage.getItem("kontractai_token");
      const fd = new FormData();
      fd.append("files", file);
      fd.append("source_request_id", requestId);
      const res = await fetch("/api/extraction/batch/start", { method: "POST", headers: { "Authorization": `Bearer ${token}` }, body: fd });
      const r = await res.json().catch((): any => null);
      // Accept ANY 2xx as success (the endpoint returns 201 Created with
      // { batch_id }). The old `r?.batchId` check mis-read a real 201 success as
      // "HTTP 201" failure AND left the awaiting row in place (Issue 3).
      if (res.ok) {
        loadQueue();
        if (onNavigate) onNavigate('import');
      } else {
        alert(`Import failed: ${r?.error || `HTTP ${res.status}`}`);
      }
    } catch (err: any) {
      alert(`Import failed: ${err.message}`);
    } finally {
      setAwaitingUploading(null);
      setDragOver(null);
    }
  };

  const importToRepo = async (envelopeId: string) => {
    if (!window.confirm("Send this signed document to AI Import for extraction and review?")) return;
    setImporting(envelopeId);
    const r = await apiCall("/esign/import-to-repository", { method: "POST", body: JSON.stringify({ envelopeId }) });
    setImporting(null);
    if (r?.batchId) {
      setQueue(prev => prev.filter((q: any) => q.id !== envelopeId));
      setSelected(prev => { const next = new Set(prev); next.delete(envelopeId); return next; });
      if (onNavigate) onNavigate('import');
    } else {
      alert(r?.error || "Import failed");
    }
  };

  const bulkImport = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Send ${selected.size} document(s) to AI Import for extraction and review?`)) return;
    setImporting('bulk');
    const envelopeIds = [...selected];
    let success = 0;
    const errors = [];
    const token = localStorage.getItem("kontractai_token");
    for (let i = 0; i < envelopeIds.length; i++) {
      const envelopeId = envelopeIds[i];
      try {
        const res = await fetch("/api/esign/import-to-repository", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ envelopeId }),
        });
        const r = await res.json().catch((): any => null);
        // Any 2xx = queued (import-to-repository returns { batchId }); don't gate
        // success on a specific body field (Issue 3 sweep — same narrow-check
        // family as importExecuted).
        if (res.ok) {
          success++;
        } else {
          const msg = r?.error || `HTTP ${res.status}`;
          errors.push(msg);
          console.error(`[BulkImport] ${envelopeId}: ${msg}`);
        }
      } catch (err: any) {
        errors.push(err.message);
        console.error(`[BulkImport] ${envelopeId} failed:`, err);
      }
      // Delay between imports to stagger extraction workers and avoid rate limits
      if (i < envelopeIds.length - 1) await new Promise(res => setTimeout(res, 1000));
    }
    setImporting(null);
    setSelected(new Set());
    if (success > 0) {
      if (errors.length > 0) alert(`${success} queued successfully, ${errors.length} failed:\n${errors.join('\n')}`);
      loadQueue();
      if (onNavigate) onNavigate('import');
    } else {
      alert(`No documents were queued. Errors:\n${errors.join('\n')}`);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.txM }}>Loading execution queue...</div>;

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "32px 40px", maxWidth: 960, margin: "0 auto" }}>
      <SectionTitle sub={`${queue.length} document${queue.length !== 1 ? "s" : ""} awaiting import`} right={<>
        {completedRows.length > 0 && <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.txD, cursor: "pointer" }}><input type="checkbox" checked={completedRows.length > 0 && selected.size === completedRows.length} onChange={toggleAll} /> Select All</label>}
        {selected.size > 0 && <Btn v="primary" sz="sm" disabled={importing === 'bulk'} onClick={() => { bulkImport(); }} style={{ backgroundColor: T.grn }}>{importing === 'bulk' ? 'Sending...' : `Send ${selected.size} to AI Import`}</Btn>}
        <RefreshBtn onClick={() => { loadQueue(); }} />
      </>}>Execution Queue</SectionTitle>
      {queue.length === 0 ? (
        <Card style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }}>✍</div>
          <div style={{ fontSize: 14, color: T.txM, fontWeight: 500 }}>No signed documents pending import</div>
          <div style={{ fontSize: 12, color: T.txD, marginTop: 6 }}>Completed DocuSign envelopes will appear here for review and import into the contract repository.</div>
        </Card>
      ) : queue.map(item => {
        // CAUSE 4 — awaiting row: counterparty-handled signing, no executed
        // document yet. Distinct row: Import button + drop zone, NO View PDF /
        // Send to AI Import / checkbox until a signed copy is supplied.
        if (item.row_type === 'awaiting') {
          const rid = item.request_id || item.id;
          const busy = awaitingUploading === rid;
          return (
            <Card key={`await-${rid}`} style={{ marginBottom: 12, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.tx, marginBottom: 4 }}>{item.request_title || "Untitled request"}</div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {item.counterparty && <span style={{ fontSize: 11, color: T.txM }}>Counterparty: <strong style={{ color: T.tx }}>{item.counterparty}</strong></span>}
                    {item.request_type && <span style={{ fontSize: 11, color: T.txM }}>Type: <strong style={{ color: T.tx }}>{item.request_type}</strong></span>}
                  </div>
                </div>
                <Badge c={T.ylw} b={T.ylwS}>Awaiting Executed Copy</Badge>
              </div>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(rid); }}
                onDragLeave={() => setDragOver(d => d === rid ? null : d)}
                onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f && !busy) importExecuted(rid, f); }}
                style={{ border: `2px dashed ${dragOver === rid ? T.acc : T.brd}`, borderRadius: 8, padding: "18px 12px", textAlign: "center", backgroundColor: dragOver === rid ? T.accS : T.bg3, transition: "all .15s" }}
              >
                <div style={{ fontSize: 12, color: T.txM, marginBottom: 10 }}>
                  {busy ? "Uploading and starting OCR + AI import…" : "Drag & drop the fully executed agreement here, or"}
                </div>
                {!busy && (
                  <label style={{ display: "inline-block" }}>
                    <input type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) importExecuted(rid, f); e.currentTarget.value = ""; }} />
                    <span style={{ display: "inline-block", padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#fff", backgroundColor: T.grn, borderRadius: 6, cursor: "pointer" }}>Import Fully Executed Agreement</span>
                  </label>
                )}
              </div>
            </Card>
          );
        }
        const signers = typeof item.signers === "string" ? JSON.parse(item.signers) : (item.signers || []);
        return (
          <Card key={item.id} style={{ marginBottom: 12, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSelect(item.id)} style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer" }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.tx, marginBottom: 4 }}>{item.request_title || item.envelope_id}</div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {item.counterparty && <span style={{ fontSize: 11, color: T.txM }}>Counterparty: <strong style={{ color: T.tx }}>{item.counterparty}</strong></span>}
                  {item.request_type && <span style={{ fontSize: 11, color: T.txM }}>Type: <strong style={{ color: T.tx }}>{item.request_type}</strong></span>}
                  {item.company_entity && <span style={{ fontSize: 11, color: T.txM }}>Entity: <strong style={{ color: T.tx }}>{item.company_entity}</strong></span>}
                </div>
              </div>
              </div>
              <Badge c={T.grn} b={T.grnS}>Completed</Badge>
            </div>
            <div style={{ display: "flex", gap: 16, marginBottom: 10, fontSize: 11, color: T.txD }}>
              {item.sent_by_name && <span>Sent by: {item.sent_by_name}</span>}
              {item.sent_at && <span>Sent: {fmtDate(item.sent_at)}</span>}
              {item.completed_at && <span>Completed: {fmtDate(item.completed_at)}</span>}
            </div>
            {signers.length > 0 && (
              <div style={{ marginBottom: 10, padding: "8px 12px", backgroundColor: T.bg3, borderRadius: 6, border: `1px solid ${T.brd}` }}>
                <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Signers</div>
                {signers.map((s: any, i: number) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: T.tx, marginBottom: 2 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: T.grn, display: "inline-block" }} />
                    <span style={{ fontWeight: 500 }}>{s.name}</span>
                    <span style={{ color: T.txD }}>{s.email}</span>
                    {s.signedDateTime && <span style={{ marginLeft: "auto", fontSize: 10, color: T.txD }}>Signed {fmtDate(s.signedDateTime)}</span>}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {item.completed_document_path && <Btn v="secondary" sz="sm" onClick={() => window.open(`/api/files/view?path=${encodeURIComponent(item.completed_document_path)}`, "_blank")}>View PDF</Btn>}
              <Btn v="primary" sz="sm" disabled={importing === item.id} onClick={() => importToRepo(item.id)} style={{ backgroundColor: T.grn, borderColor: T.grn }}>{importing === item.id ? "Sending..." : "Send to AI Import"}</Btn>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

/* ═══════════════════ NOTIFICATIONS ═══════════════════ */
